import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import {
    DA_CategoryNavigation,
    DA_CategoryNavigationProps,
} from "@molecules/CategoryNavigation/CategoryNavigation.component";
import { DA_CategoryLink } from "@interfaces/categoryLink.interface";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { DA_LinkSlider } from "@molecules/LinkSlider/LinkSlider.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useInView } from "react-intersection-observer";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_NewProductSimple } from "@danishagro/shared/src/interfaces/productv2.interface";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_SwitchElement } from "@danishagro/shared/src/components/atoms/SwitchElement/SwitchElement.component";
import { useSearchParams } from "react-router-dom";
import { useFavorites } from "@danishagro/shared/src/contexts/favorites.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { usePage } from "@danishagro/shared/src/contexts/page.context";
import { Helmet } from "react-helmet";
import { DA_ProductPrice } from "@danishagro/shared/src/interfaces/price.interface";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { CategorySkeleton } from "./components/CategorySkeltons/CategorySkeltons.components";
import { ProductListHeader } from "./components/ProductListHeader/ProductListHeader.component";
import { RelewiseFacets } from "./components/RelewiseFacets/RelewiseFacets.component";
import { RelewiseProductList } from "./components/RelewiseProductList/RelewiseProductList.component";
import { DA_FilteringMobileDrawer } from "./components/FilteringMobileDrawer/FilteringMobileDrawer.component";
import { DA_CategoryProductList } from "./components/CategoryProductList/CategoryProductList.component";
// eslint-disable-next-line css-modules/no-unused-class -- titleWrapper, categoryHeading used by SearchResults pages
import S from "./RelewiseCategoryV1.module.scss";

export enum PaginationViewState {
    Initial = "INITIAL",
    None = "NONE",
    Full = "FULL",
    Loading = "LOADING",
}
export interface V1Props extends Omit<DA_CategoryNavigationProps, "heading"> {
    categoryId: string;
    facetGroups?: string[];
    enableQuickOrder?: boolean;
    subCategories?: DA_CategoryLink[];
}

export const V1 = ({
    title,
    categoryId,
    subCategories,
    enableQuickOrder,
    ...navigationProps
}: V1Props) => {
    const [showFacets] = useState(true);
    const [paginationViewState, setPaginationViewState] = useState<string>(
        PaginationViewState.Initial
    );

    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const { currentPathDetails } = usePage();

    const {
        fetchProducts,
        pageCount,
        facets,
        products,
        totalProductCount,
        selectedFacets,
        selectedSortingOption,
        page,
        pageSize,
        initialDefaultPageSize,
        initialQuickOrderPageSize,
        resetFilters,
        setPage,
        setPageSize,
        handleProductResponse,
        isLoading,
    } = useRelewise();
    const { isMobileOrTablet } = useScreen();
    const { getDictionaryString } = useTranslations();
    const hasChildren = useMemo(() => subCategories && subCategories.length > 0, [subCategories]);

    const { displayFavoritesList, cvrAndCustomerNumbers, customerNumber, currentCulture } =
        useAppData();

    // Initialize refreshFavorites as a no-op function
    const defaultRefreshFavorites = useCallback(() => {}, []);

    let refreshFavorites = defaultRefreshFavorites;

    // Wrap the useFavorites call in a try-catch block to avoid errors if the provider is not available
    try {
        const favoritesContext = useFavorites();
        if (displayFavoritesList) {
            refreshFavorites = favoritesContext.refreshFavorites;
        }
    } catch (error) {
        refreshFavorites = defaultRefreshFavorites; // Use the stable no-op function if useFavorites is not available
    }

    const checkForVetMedication = (products: DA_NewProductSimple[]) => {
        // Iterate over each object in the array
        for (const obj of products) {
            // Check if both vetMedicalLink.value and vetMedicationLogo.value have values
            if (obj.data?.vetMedicalLink?.value && obj.data?.vetMedicationLogo?.value) {
                return true; // Return true if both have values
            }
        }
        return false; // Return false if no object has both values
    };
    const hasVetMedication = checkForVetMedication(products);

    // Get vetMedication Logo
    const getVetMedicationLogo = (data) => {
        for (let i = 0; i < data.length; i++) {
            if (data[i].data.vetMedicationLogo) {
                return data[i].data.vetMedicationLogo;
            }
        }
        return null; // Return null if vetMedicationLogo is not found in any object
    };
    const vetMedicationLogo = getVetMedicationLogo(products);

    // Get vedMedication Link
    const getVetMedicationLink = (data) => {
        for (let i = 0; i < data.length; i++) {
            if (data[i].data?.vetMedicalLink) {
                return data[i].data?.vetMedicalLink;
            }
        }
        return null; // Return null if vetMedicationLogo is not found in any object
    };
    const vetMedicationLink = getVetMedicationLink(products);

    const [searchParams, setSearchParams] = useSearchParams();
    const hasQuickOrderParam = searchParams.has("quickorder");
    const [showQuickerOrder, setShowQuickerOrder] = useState(
        () => JSON.parse(localStorage.getItem("quickOrder")) || false
    );

    const { ref: bottomDiv, inView } = useInView({
        rootMargin: "200px",
    });

    const initialRequest = useRef(true);
    const isMounting = useRef(true);
    const lastCategoryId = useRef(categoryId);

    const loadMore = useCallback(() => {
        if (page < pageCount) {
            setPaginationViewState(PaginationViewState.Loading);
            setPage(page + 1);
            fetchProducts(page + 1).then((response) => {
                handleProductResponse(response, true);
                if (displayFavoritesList) {
                    refreshFavorites();
                }
                setPaginationViewState(PaginationViewState.None);
            });
        }
    }, [
        displayFavoritesList,
        fetchProducts,
        handleProductResponse,
        page,
        pageCount,
        refreshFavorites,
        setPage,
    ]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (enableQuickOrder && showQuickerOrder && !hasQuickOrderParam) {
            params.set("quickorder", "true");
            setPageSize(initialQuickOrderPageSize);
            setSearchParams(params);

            // Let selectedFacets/selectedSortingOption useEffect handle the fetch to avoid duplicates
        }

        if ((!enableQuickOrder || !showQuickerOrder) && hasQuickOrderParam) {
            params.delete("quickorder");
            setPageSize(initialDefaultPageSize);
            setSearchParams(params);
        }
    }, [
        enableQuickOrder,
        showQuickerOrder,
        hasQuickOrderParam,
        searchParams,
        setSearchParams,
        setPageSize,
        fetchProducts,
        handleProductResponse,
        pageSize,
        initialQuickOrderPageSize,
        initialDefaultPageSize,
        resetFilters,
        isLoading,
    ]);

    useEffect(() => {
        localStorage.setItem("quickOrder", JSON.stringify(showQuickerOrder));
    }, [showQuickerOrder]);

    // Set initial loading to false only when products actually arrive OR when we confirm there are no products
    useEffect(() => {
        if (initialLoading && !initialRequest.current) {
            // If we have products, stop loading
            if (products.length > 0) {
                setInitialLoading(false);
            }
            // If we have a response (totalProductCount is set) but no products, also stop loading
            else if (totalProductCount === 0) {
                setInitialLoading(false);
            }
        }
    }, [products, initialLoading, initialRequest, totalProductCount]);

    const handleQuickOrderChange = (enabled: boolean) => {
        resetFilters();
        setShowQuickerOrder(enabled);
    };

    useEffect(() => {
        // Skip if this is the initial mount - the mount useEffect will handle the first fetch
        // Also skip if the category has changed (mount useEffect will handle it)
        if (
            !initialRequest.current &&
            !isMounting.current &&
            lastCategoryId.current === categoryId
        ) {
            fetchProducts(1).then((response) => {
                handleProductResponse(response, false);
                setPaginationViewState(PaginationViewState.None);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cvrAndCustomerNumbers, customerNumber]);

    useEffect(() => {
        if (page === pageCount) {
            setPaginationViewState(PaginationViewState.Full);
        }
    }, [page, pageCount, paginationViewState]);

    useEffect(() => {
        if (inView && paginationViewState === PaginationViewState.None && pageCount > page) {
            loadMore();
        }
    }, [inView, loadMore, page, pageCount, paginationViewState, setPage]);

    useEffect(() => {
        // Skip if this is the initial mount - the mount useEffect will handle the first fetch
        // Also skip if the category has changed (mount useEffect will handle it)
        if (
            !initialRequest.current &&
            !isMounting.current &&
            lastCategoryId.current === categoryId
        ) {
            setPaginationViewState(PaginationViewState.Loading);

            //Always reset to page 1 on sorting or facetting
            setPage(1);
            fetchProducts(1, pageSize).then((response) => {
                handleProductResponse(response, false);
                setPaginationViewState(PaginationViewState.None);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFacets, selectedSortingOption, pageSize]);

    useEffect(() => {
        if (!initialRequest.current && paginationViewState === PaginationViewState.None) {
            setPaginationViewState(PaginationViewState.Loading);
            fetchProducts().then((response) => {
                handleProductResponse(response, false);
                setPaginationViewState(PaginationViewState.None);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        // Prevent duplicate calls during React Strict Mode double-mount
        if (!initialRequest.current) {
            console.log(
                "[B2B Category] Mount useEffect - skipping because initialRequest is already false"
            );
            return;
        }

        const params = new URLSearchParams(location.search);
        const currentPageSize = params.has("quickorder")
            ? initialQuickOrderPageSize
            : initialDefaultPageSize;

        if (params.has("quickorder")) {
            setPageSize(initialQuickOrderPageSize);
        }

        fetchProducts(1, currentPageSize).then((response) => {
            handleProductResponse(response, false);
            setPaginationViewState(PaginationViewState.None);
            if (displayFavoritesList) {
                refreshFavorites();
            }
            initialRequest.current = false;
            isMounting.current = false;
            lastCategoryId.current = categoryId;
            setInitialLoading(false);
        });

        const handleUnload = () => {
            resetFilters();
        };
        window.addEventListener("unload", handleUnload);
        return () => {
            window.removeEventListener("unload", handleUnload);
            handleUnload();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastItemName = currentPathDetails.at(-1)?.name ?? "";

    // PriceData is used for Structured Data for SEO
    // We get the price data from the RelewiseProductList or CategoryProductList (quickorder) component via onPricesFetched callback
    const [priceData, setPriceData] = useState<DA_ProductPrice[]>([]);

    function onPricesFetchedHandler(prices: DA_ProductPrice[]): void {
        setPriceData(prices);
    }

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        url: window.location.href,
        numberOfItems: products?.length || 0,
        category: lastItemName,
        itemListElement: products?.map((product, index) => {
            const priceInfo = (priceData || []).find(
                (p) => p && p.productNumber && p.productNumber === product.masterProductNumber
            );
            return {
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@type": "Product",
                    image: B2bImageSrc(product.imageUrl, ImageConfig.PdpMainImage),
                    url: product.url,
                    name: product.displayName,
                    sku: product.masterProductNumber,
                    offers: {
                        "@type": "Offer",
                        priceCurrency: priceInfo?.currency || "DKK",
                        price: formatNumber(
                            priceInfo?.discountPrice > 0
                                ? priceInfo?.discountPrice
                                : priceInfo?.priceWithoutVat,
                            currentCulture,
                            {
                                decimals: 2,
                            }
                        ),
                    },
                },
            };
        }),
    };

    // useEffect(() => {
    //     if (categoryId) {
    //         console.log('categoryId changed UE', categoryId);
    //         changeCategory(categoryId);
    //         // setInitialLoading(true);
    //         // setPaginationViewState(PaginationViewState.Loading);
    //         // changeCategory(categoryId).then((response) => {
    //         //     console.log('changeCategory response', response);
    //         //     handleProductResponse(response, false);
    //         //     initialRequest.current = false;
    //         // }).catch((error) => {
    //         //     console.error("Error changing category:", error);
    //         // }).finally(() => {
    //         //     setInitialLoading(false);
    //         //     setPaginationViewState(PaginationViewState.None);
    //         // });
    //         // fetchProducts(1).then((response) => {
    //         //     handleProductResponse(response, false);
    //         //     setPaginationViewState(PaginationViewState.None);
    //         //     if (displayFavoritesList) {
    //         //         refreshFavorites();
    //         //     }
    //         //     initialRequest.current = false;
    //         //     setInitialLoading(false);
    //         // });
    //     }
    // }, [categoryId, changeCategory]);

    // useEffect(() => {
    //     if (products.length) {
    //         console.log('useEffect products', products);
    //         setInitialLoading(false);
    //         setPaginationViewState(PaginationViewState.None);
    //     }
    // }, [products]);

    return (
        <article>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            {/* <div className={S.titleWrapper}>
                <div className={S.categoryHeading}>
                    {title ? (
                        <DA_Title fullWidth h1>
                            {title}
                        </DA_Title>
                    ) : null}
                </div>

                {enableQuickOrder && !isMobileOrTablet && (
                    <div className={S.toggleWrapper}>
                        <label htmlFor="switchQuickOrder">
                            <span>{getDictionaryString("categoryquickorder")}</span>
                        </label>
                        <DA_SwitchElement
                            id="switchQuickOrder"
                            checked={showQuickerOrder}
                            onChange={(enabled) => {
                                handleQuickOrderChange(enabled);
                            }}
                        />
                    </div>
                )}
            </div> */}

            {enableQuickOrder && !isMobileOrTablet && (
                <div className={S.toggleWrapper}>
                    <label htmlFor="switchQuickOrder">
                        <span>{getDictionaryString("categoryquickorder")}</span>
                    </label>
                    <DA_SwitchElement
                        id="switchQuickOrder"
                        checked={showQuickerOrder}
                        onChange={(enabled) => {
                            handleQuickOrderChange(enabled);
                        }}
                    />
                </div>
            )}
            <div className={cn(S.container, initialLoading && S.dimmed)}>
                <div className={S.innerContainer}>
                    {initialLoading ? (
                        <CategorySkeleton />
                    ) : enableQuickOrder && showQuickerOrder ? (
                        <>
                            {hasChildren ? (
                                <DA_CategoryProductList
                                    items={products}
                                    onPricesFetched={onPricesFetchedHandler}
                                    categoryId={categoryId}
                                    isQuickOrder={showQuickerOrder}
                                    {...navigationProps}
                                    relatedCategoryLinks={subCategories}
                                    topCategoryLink={navigationProps.relatedCategoryLinks.find(
                                        (cat) => cat.currentPage
                                    )}
                                />
                            ) : (
                                <DA_CategoryProductList
                                    items={products}
                                    onPricesFetched={onPricesFetchedHandler}
                                    categoryId={categoryId}
                                    isQuickOrder={showQuickerOrder}
                                    {...navigationProps}
                                    topCategoryLink={{
                                        id: "group0",
                                        name: navigationProps.parentCategoryName,
                                        url: navigationProps.parentCategoryLink,
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        <section className={S.content}>
                            {(hasChildren ||
                                navigationProps.relatedCategoryLinks?.length > 0 ||
                                (showFacets && facets)) && (
                                <div className={S.menuWrapper}>
                                    <div className={S.menu}>
                                        {hasChildren && isMobileOrTablet ? (
                                            <DA_LinkSlider
                                                title={getDictionaryString("other categories")}
                                                items={subCategories}
                                            />
                                        ) : hasChildren ? (
                                            <DA_CategoryNavigation
                                                heading={getDictionaryString("categories")}
                                                title={title}
                                                {...navigationProps}
                                                relatedCategoryLinks={subCategories}
                                            />
                                        ) : navigationProps.relatedCategoryLinks?.length > 0 &&
                                          isMobileOrTablet ? (
                                            <DA_LinkSlider
                                                title={getDictionaryString("other categories")}
                                                items={navigationProps.relatedCategoryLinks.filter(
                                                    ({ currentPage }) => !currentPage
                                                )}
                                            />
                                        ) : navigationProps.relatedCategoryLinks?.length > 0 ? (
                                            <DA_CategoryNavigation
                                                heading={getDictionaryString("categories")}
                                                title={navigationProps.parentCategoryName}
                                                {...navigationProps}
                                            />
                                        ) : null}

                                        {showFacets && facets && !isMobileOrTablet ? (
                                            <RelewiseFacets facets={facets} />
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            <div className={S.results}>
                                {products.length > 0 ? (
                                    <>
                                        {hasVetMedication && (
                                            <DA_BasicLink
                                                className={cn(S.vetMedicationLink)}
                                                href={vetMedicationLink?.value}
                                            >
                                                <DA_Image
                                                    src={vetMedicationLogo?.value}
                                                    width={120}
                                                    alt={getDictionaryString(
                                                        "VetMedicationImageAltText"
                                                    )}
                                                    className={S.vetMedicationImage}
                                                />
                                            </DA_BasicLink>
                                        )}
                                        <ProductListHeader totalProductCount={totalProductCount} />
                                        <RelewiseProductList
                                            products={products}
                                            onPricesFetched={(prices) => setPriceData(prices)}
                                            categoryId={categoryId}
                                        />

                                        <div
                                            className={
                                                paginationViewState === PaginationViewState.Full
                                                    ? S.loadMoreWrapperFull
                                                    : S.loadMoreWrapper
                                            }
                                        >
                                            {paginationViewState === PaginationViewState.Loading ? (
                                                <DA_Spinner className={S.spinner} />
                                            ) : null}

                                            {paginationViewState === PaginationViewState.None &&
                                            page < pageCount ? (
                                                <DA_Button
                                                    onClick={() => loadMore()}
                                                    ref={bottomDiv}
                                                    title={getDictionaryString("loadMoreProducts")}
                                                />
                                            ) : null}
                                        </div>
                                    </>
                                ) : (
                                    <div className={S.placeholder}>
                                        <DA_Icon name={DA_IconNames.Leaf} className={S.leaf} />
                                        <DA_Title h3 tag={DA_TitleTag.P}>
                                            {getDictionaryString("no products")}
                                        </DA_Title>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {isMobileOrTablet && <DA_FilteringMobileDrawer facets={facets} />}
                </div>
            </div>
        </article>
    );
};
